/**
 * Which clauses of a parked delivery did a reply NOT carry? — the safety net behind an aside: the reflex answers and
 * continues the unheard remainder itself; whatever it dropped stays parked (for a later "go on"/aside), never replayed.
 *
 * Judged per CLAUSE (live 20260923-192543 msg 26: pricing and the waitlist were one sentence — scored whole, a
 * pricing-only answer counted the waitlist as said). Lexical overlap can't judge paraphrase ("people are queued up"
 * for "pulling developers off a waitlist" shares no word), so a high overlap is SAID on a fast path and every other
 * clause goes to the decider (Jev noul, one batched call). No decider / timeout / error → only near-zero overlap
 * counts as unsaid: repeating is worse than dropping (the full text stays in the transcript).
 */
import { forComponent } from '../logging';
import type { Decider } from './instinct';

const log = forComponent('Unsaid');

const STOP = new Set('that this with have from they them their there then than what when were will would your about into just also been some only over more most very which while after before because these those does here'.split(' '));
const contentStems = (s: string) => (s.toLowerCase().match(/[a-z0-9']+/g) ?? []).filter((w) => w.length >= 4 && !STOP.has(w)).map((w) => w.slice(0, 4));

/** Overlap at/above this → SAID without a call. */
export const SAID_OVERLAP = 0.6;
/** Fallback only (no verdict): overlap below this (~one shared stem in five) → unsaid; the middle counts as said. */
export const UNSAID_OVERLAP = 0.25;
/** Jev noul (main point conveyed) below this → unsaid. Live eval 2026-09-23 (msg-26 clauses, 2 runs): drops ≤0.10, partial paraphrases 0.48–0.53 — cut low, a repeat is worse than a drop. */
export const CONVEYED_MIN = 0.3;

/** Sentences, split further at `;` and `, and` / `, but` joins. The joiner stays on the clause, so adjacent clauses re-join as written. */
export function splitClauses(text: string): string[] {
  return text.split(/(?<=[.!?…])\s+|(?<=;)\s+|(?<=,)\s+(?=(?:and|but)\b)/i).map((s) => s.trim()).filter((s) => contentStems(s).length > 0);
}

const overlap = (clause: string, said: Set<string>) => { const w = contentStems(clause); return w.filter((x) => said.has(x)).length / w.length; };

const INSTRUCTIONS = 'A voice assistant was interrupted mid-delivery; ASSISTANT_SAID is what it then said aloud. ' +
  'Did the MAIN POINT of this clause of the unheard delivery reach the listener through ASSISTANT_SAID — would hearing the clause too add nothing important? ' +
  'Different words, casual phrasing, rounder numbers, or a missing minor detail (a name, an exact figure) still count as conveyed. ' +
  'Not conveyed only when the clause\'s main point is absent from ASSISTANT_SAID, even if a neighbouring point is there.';

/** The clauses of `pieces` that `spoken` did not carry, in order. Never throws. */
export async function unsaidClauses(pieces: string[], spoken: string, decider?: Decider, timeoutMs = 5000): Promise<string[]> {
  const said = new Set(contentStems(spoken));
  const open = splitClauses(pieces.join(' ')).map((c) => ({ c, r: overlap(c, said) })).filter((x) => x.r < SAID_OVERLAP);
  if (!open.length) return [];
  const fallback = () => open.filter((x) => x.r < UNSAID_OVERLAP).map((x) => x.c);
  if (!decider) return fallback();
  const state: Record<string, string> = { assistant_said: spoken };
  const questions: Record<string, unknown> = {};
  open.forEach((x, i) => {
    state[`c${i}`] = x.c;
    questions[`c${i}`] = { type: 'noul', instructions: `${INSTRUCTIONS} The clause is c${i}.`, criteria: { true: `the main point of c${i} reached the listener`, false: `the main point of c${i} is missing` } };
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const { answers } = await Promise.race([
      decider.decide({ state, questions }),
      new Promise<never>((_, rej) => { timer = setTimeout(() => rej(new Error(`no verdict in ${timeoutMs}ms`)), timeoutMs); }),
    ]);
    return open.filter((_, i) => {
      const p = Number((answers[`c${i}`] as unknown as { noul?: number } | undefined)?.noul);
      if (!Number.isFinite(p)) throw new Error(`no noul for c${i}`);
      return p < CONVEYED_MIN;
    }).map((x) => x.c);
  } catch (e) {
    log.warn(`unsaid judge failed — only near-zero-overlap clauses count as unsaid: ${(e as Error).message}`);
    return fallback();
  } finally { clearTimeout(timer); }
}
